import { Injectable } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { Restricao } from '../permissoes-de-sistema/permissoes-de-sistema.service';

/** Rota atribuída a itens que só agrupam filhos — não navegam para lugar nenhum. */
export const MENU_SEM_ROTA = './#';

export interface MenuOption {
  id: string;
  description: string;
  pathIcon: any;
  route: string;
  ordem: number;
  hasChildren: boolean;
  children: MenuOption[];
  restricoes: Restricao[];
}

@Injectable({
  providedIn: 'root',
})
export class SidebarService {
  constructor(private sanitizer: DomSanitizer) {}

  getMenuOptions(apiData: any): MenuOption[] {
    const menuOptions: MenuOption[] = [];

    const profileUser = apiData.profileUser;

    if (!profileUser || !profileUser.permissaoPerfis) {
      return menuOptions;
    }
    // Process each 'funcionalidade' in permissaoPerfis
    profileUser.permissaoPerfis.forEach((permissaoPerfil: any) => {
      const funcionalidade = permissaoPerfil.funcionalidade;

      // Create the main menu item with recursive children loading
      const menuOption: MenuOption = {
        id: funcionalidade.id,
        description: funcionalidade.nome,
        ordem: funcionalidade.ordem,
        pathIcon: this.getSafeUrl(this.getIconPath(funcionalidade.nome)),
        route: funcionalidade.url || MENU_SEM_ROTA,
        hasChildren:
          funcionalidade.subFuncionalidades &&
          funcionalidade.subFuncionalidades.length > 0,
        children: this.mapSubFuncionalidades(funcionalidade.subFuncionalidades),
        restricoes: funcionalidade.restricoes || [],
      };

      menuOptions.push(menuOption);
    });

    // Sort the main menu options by 'ordem'
    menuOptions.sort((a, b) => a.ordem - b.ordem);

    return this.removeEmptyBranches(menuOptions);
  }

  /**
   * Descarta os itens que não levam a lugar nenhum: sem rota própria e sem
   * nenhum descendente permitido ao perfil do usuário.
   */
  private removeEmptyBranches(options: MenuOption[]): MenuOption[] {
    return options
      .map((option) => {
        const children = this.removeEmptyBranches(option.children);

        return { ...option, children, hasChildren: children.length > 0 };
      })
      .filter((option) => option.hasChildren || this.isNavigable(option));
  }

  private isNavigable(option: MenuOption): boolean {
    return !!option.route && option.route !== MENU_SEM_ROTA;
  }

  private mapSubFuncionalidades(subFuncionalidades: any[]): MenuOption[] {
    // Copy first — the menu response is shared via shareReplay(1).
    return [...subFuncionalidades]
      .sort((a: any, b: any) => a.ordem - b.ordem) // Sort by 'ordem'
      .map((subFunc: any) => ({
        id: subFunc.id,
        description: subFunc.nome,
        ordem: subFunc.ordem,
        pathIcon: '',
        route: subFunc.url || MENU_SEM_ROTA,
        hasChildren:
          subFunc.subFuncionalidades && subFunc.subFuncionalidades.length > 0,
        children: subFunc.subFuncionalidades
          ? this.mapSubFuncionalidades(subFunc.subFuncionalidades)
          : [],
        restricoes: subFunc.restricoes || [],
      }));
  }

  getIconPath(description: string): string {
    const iconMap: { [key: string]: string } = {
      'Gestão de usuário': 'assets/create-user/svgs/user_conf.svg',
      Especificações: 'assets/create-user/svgs/cadastro.svg',
      Configuração: 'assets/create-user/svgs/vector.svg',
      Cadastro: 'assets/create-user/svgs/conjunto.svg',
      Operacional: 'assets/create-user/svgs/contrato.svg',
      Assinatura: 'assets/create-user/svgs/assinatura.svg',
      Táxi: 'assets/create-user/svgs/taxi.svg',
      Metropolitano: 'assets/navbar/metropolitano.svg',
    };

    return iconMap[description] || 'assets/create-user/svgs/conjunto.svg';
  }

  getSafeUrl(path: string): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(path);
  }
}
